import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { flatMap, keyBy, unionBy } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, publishLast, refCount } from 'rxjs/operators';
import { dataMember, deserialize, required, typedArray } from 'santee-dcts';
import { Settings } from '../core';
import { Category, CategoryNaturalKey } from './models/category';

@Injectable()
export class CategoriesStorage {
    private readonly generalCategoriesInternal: Observable<Category[]>;
    private readonly siteSpecificCategoriesInternal: Observable<Category[]>;
    private readonly activityReportsCategoriesInternal: Observable<Category[]>;
    private readonly categoriesById: Observable<{ [categoryId: string]: Category }>;

    constructor(private http: HttpClient, private settings: Settings) {
        this.generalCategoriesInternal = this.loadCategoriesInternal('general')
            .pipe(
                publishLast(),
                refCount()
            );

        this.siteSpecificCategoriesInternal = this.loadCategoriesInternal('site-specific')
            .pipe(
                publishLast(),
                refCount()
            );

        this.activityReportsCategoriesInternal = this.loadCategoriesInternal('activity-reports')
            .pipe(
                publishLast(),
                refCount()
            );

        this.categoriesById = this.all
            .pipe(
                map((x: Category[]) => {
                    const mainCats = x.reduce(
                        (a, v) => this.unwindCategories(a, v),
                        []);
                    return keyBy(mainCats, c => c.categoryId);
                }),
                publishLast(),
                refCount()
            );
    }

    public get generalAndSiteSpecific(): Observable<Category[]> {
        return combineLatest(this.generalCategoriesInternal, this.siteSpecificCategoriesInternal)
            .pipe(
                map(([a, cv]) => [...a, ...cv], [])
            );
    }

    public get generalCategories(): Observable<Category[]> {
        return this.generalCategoriesInternal;
    }

    public get siteSpecificCategories(): Observable<Category[]> {
        return this.siteSpecificCategoriesInternal;
    }

    private get all(): Observable<Category[]> {
        return combineLatest(this.generalAndSiteSpecific, this.activityReportCategories)
            .pipe(map(([_, __]) => unionBy(_, __, c => c.categoryId)));
    }

    public get activityReportCategories(): Observable<Category[]> {
        return this.activityReportsCategoriesInternal;
    }

    public get siteDocumentsCategories(): Observable<Category[]> {
        return this.findCategoriesByIds(['site-communications', 'site-actions']);
    }

    public get studyDocumentsCategories(): Observable<Category[]> {
        return this.findCategoriesByIds(['lab-manuals', 'training-materials', 'sponsor-communications-e-learning'])
            .pipe(map(categories => {
                const labReports = categories.pop();
                const docCategories = flatMap(categories, c => c.children);
                docCategories.push(labReports);
                return docCategories;
            }));
    }

    public findCategoryById(categoryId: CategoryNaturalKey): Observable<Category | undefined> {
        return this.categoriesById.pipe(map(categories => categories[categoryId]));
    }

    public findCategoriesByIds(categoryIds: CategoryNaturalKey[]): Observable<Category[]> {
        return this.categoriesById.pipe(map(categories => categoryIds.map(id => categories[id])));
    }

    public getChildCategoriesById(categoryId: CategoryNaturalKey): Observable<Category[] | null> {
        return this.findCategoryById(categoryId).pipe(map(category => {
            if (!category) {
                return null;
            }

            return category.children;
        }));
    }

    private unwindCategories(acc: Category[], category: Category) {
        acc.push(category);
        if (category.children) {
            category.children.reduce((a, v) => this.unwindCategories(a, v), acc);
        }
        return acc;
    }

    private loadCategoriesInternal(type: string) {
        return this.http.get(`${this.settings.apiUrl}/categories/${type}`)
            .pipe(
                map((response) => {
                    const categories = deserialize(response, CategoriesReadModel).categories;
                    this.fillParents(null, categories);
                    return categories;
                }));
    }

    private fillParents(parent: Category, categories: Category[]) {
        categories.forEach(category => {
            category.parent = parent;
            this.fillParents(category, category.children);
        });
    }
}

class CategoriesReadModel {

    @dataMember()
    @required()
    @typedArray(Category)
    public categories: Category[];
}